import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Layout/Header';
import { leadsService, AdminLeadRow } from '../services/leads.service';
import { downloadVehicleLeadsExcel } from '../utils/exportVehicleLeadsExcel';
import './AdminLeads.css';

const formatWhen = (iso: string) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
};

const AdminLeads: React.FC = () => {
  const [rows, setRows] = useState<AdminLeadRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const list = await leadsService.listForAdmin();
        setRows(list);
      } catch (err: unknown) {
        const msg =
          err && typeof err === 'object' && 'message' in err
            ? String((err as { message?: string }).message)
            : 'Failed to load leads';
        setError(msg);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div className="admin-leads-page">
      <Header />
      <div className="admin-leads-container">
        <Link to="/admin/analytics" className="admin-leads-back">
          ← Analytics dashboard
        </Link>
        <div className="admin-leads-heading">
          <div className="admin-leads-heading-row">
            <div>
              <h1>Vehicle Leads — submissions</h1>
              <p>
                All referral forms with the employee who submitted each lead (name, employee number, branch, and
                contact number).
              </p>
            </div>
            {!loading && !error && rows.length > 0 && (
              <div className="admin-leads-export">
                <button
                  type="button"
                  className="admin-leads-export-btn"
                  onClick={() => void downloadVehicleLeadsExcel(rows)}
                >
                  Download Excel
                </button>
              </div>
            )}
          </div>
        </div>

        {loading && <div className="admin-leads-state">Loading leads…</div>}
        {error && <div className="admin-leads-state admin-leads-error">{error}</div>}

        {!loading && !error && rows.length === 0 && (
          <div className="admin-leads-state">No leads submitted yet.</div>
        )}

        {!loading && !error && rows.length > 0 && (
          <div className="admin-leads-table-wrap">
            <table className="admin-leads-table">
              <thead>
                <tr>
                  <th scope="col">Submitted</th>
                  <th scope="col">Submitted by (employee)</th>
                  <th scope="col">Customer name</th>
                  <th scope="col">Customer phone</th>
                  <th scope="col">Suggested branch</th>
                  <th scope="col">Suggested model</th>
                  <th scope="col">Comments</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>{formatWhen(row.createdAt)}</td>
                    <td className="admin-leads-submitter">
                      {row.submittedBy ? (
                        <>
                          <strong>{row.submittedBy.fullName}</strong>
                          <span>Emp. {row.submittedBy.empNo}</span>
                          <span>{row.submittedBy.branch}</span>
                          <span>{row.submittedBy.telNo || '—'}</span>
                        </>
                      ) : (
                        <span style={{ color: 'var(--text-muted, #888)' }}>Unknown user</span>
                      )}
                    </td>
                    <td>{row.customerName}</td>
                    <td>{row.customerPhone}</td>
                    <td>{row.suggestedBranch}</td>
                    <td>{row.suggestedModel}</td>
                    <td className="admin-leads-comments">{row.otherComments || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLeads;
